/**
 * 이상한 방 - 앱 엔트리포인트
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 원칙 2(1절) · 5.4절 · 6.1절 · 9.4절 · 9.5절 · 11.3절
 *           `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 4.3절(탄환 경제) · 8절(판정 지표)
 *
 * ## 싱글은 "한 판"이 아니라 "10단계 커리큘럼"이다
 *
 * 한때 이 앱은 시작하자마자 **아파트 9구획 · 오브젝트 391개 · 변화 12개**를 던졌다.
 * 변화 비율 약 3%다(통상적인 틀린그림찾기는 10~20%). 개발자가 직접 플레이하고 내린 평가가
 * "엄청 어렵다, 이거 깰 수 있나"였는데, 그건 난이도 문제가 아니라 **튜토리얼 없이 최종
 * 난이도부터 시작한 것**이었다.
 *
 * 상태모델 9.5절은 완전 싱글의 포지션을 튜토리얼로 못박았다. 메인은 멀티이고 싱글은 준비 과정이다.
 * 그러므로 이 파일의 목표는 재미있는 한 판을 여는 것이 아니라 **10단계를 끝까지 통과시키는 것**이다.
 *
 * ```
 *   타이틀 → 단계 선택 → 단계 안내(준비물 3택1) → 외우기 → 수색 → 리빌 → 결과 → 다음 단계 / 다시 하기 / 선택
 *                 ↕  ↘ "매일의 방" 카드 → 오늘의 한 판(같은 날 모두 같은 방) - 로드맵 M9
 *                 ↕  ↘ "여럿이" 카드 → 로비(방 만들기 · 번호로 들어가기 · 이웃 넣기 · 준비 · 시작)
 *                 ↕                   → 한 판(외우기 → 수색 → 리빌 → 순위) → 한 판 더(다음 라운드) / 나가기(선택) - 로드맵 M2 · M6
 *           봉투 · 사물함  (선택 화면에서만 열린다. 결과 화면에는 여는 버튼이 없다 - 로드맵 M3)
 * ```
 *
 * ## 이 파일에 남은 것은 흐름과 조립뿐이다
 *
 * 게임 규칙은 `game/gameState.ts`(순수 리듀서), 단계 곡선은 `game/levels.ts`(순수 데이터),
 * 해금 규칙은 `game/progress.ts`에 있다. 그리고 조립의 각 조각은 `app/`으로 갈라져 나갔다:
 *
 * | 파일 | 무엇을 쥐는가 |
 * |---|---|
 * | `app/boot.ts` | 캔버스 찾기 · 탭 제목/문서 언어 · 배경음 첫 제스처 · 실패 배너 |
 * | `app/urlState.ts` | 주소창 읽기·쓰기. **URL 표의 원본이 여기다** |
 * | `app/stage.ts` | `Stage` 한 판의 조립·해제. `ctx`를 만들 방법이 없는 파일 |
 * | `app/inputLayer.ts` | 조작 레이어 · 캔버스 제스처 · 키보드, 그리고 게임/배치 **모드** |
 * | `app/settingsItems.ts` | 설정 항목 넷 |
 * | `app/accountMagazines.ts` | 계정 탄창(`?mags=` 강제 포함) |
 * | `app/accountEconomy.ts` | 계정 핀·봉투·사물함(`?pins=` 강제 포함). 봉투 한 장 = 저장 한 번 |
 * | `app/debugHooks.ts` | `__weirdRoom` |
 * | `app/appMode.ts` | `AppScreen`·`AppMode` 어휘 |
 * | `app/netBoot.ts` | 방 서버 주소 · 재접속 토큰(`sessionStorage`를 아는 유일한 파일) |
 * | `app/lobbyFlow.ts` | 소켓 방에 앉기까지와 대기실, 봇 판의 자동 진행 |
 * | `app/dailyFlow.ts` | 오늘의 날짜·시드·튜닝, 그리고 매일의 방 한 판의 마무리(기록·출석·핀) |
 * | `app/kitFlow.ts` | 준비물 3택1의 제시·선택·소비 |
 *
 * 여기 남은 것은 **화면 흐름**(타이틀→선택→안내→판), **무대 교체**, **진행 저장**,
 * 그리고 그 조각들을 잇는 배선이다. 새 기능을 붙일 때 이 셋 중 어느 것도 아니라면
 * 그건 `app/`의 파일이거나 아직 없는 파일이지 이 파일이 아니다.
 *
 * ## 두 층으로 갈린다 - `ctx`는 절대 다시 만들지 않는다
 *
 * 단계마다 평면도가 바뀌므로(원룸 24셀 → 아파트 80셀) 셸·조명·컷어웨이·카메라 절두체를
 * 다시 세워야 한다. 그래서 **살아남는 것**(`SceneCtx`·GLB 캐시·프레임 루프·오버레이 화면)과
 * **단계마다 다시 서는 것**(`Stage`)이 갈린다. WebGL 컨텍스트를 단계마다 새로 만들면
 * 브라우저의 컨텍스트 개수 상한(보통 8~16)에 몇 판 만에 걸리므로, `createScene`을 부르는
 * 자리는 이 파일의 한 줄뿐이다 - 자세한 근거와 그것을 지키는 검사는 `app/stage.ts`에 있다.
 *
 * ## URL이 곧 개발 도구다 (9.4절)
 *
 * 4주 계획의 병목은 코딩이 아니라 반복 횟수이고, 그래서 주소창 하나로 원하는 지점에 떨어진다.
 * **URL 전체 표는 `app/urlState.ts` 머리말에 있다** - 표와 파서를 갈라 두면 둘 중 하나가
 * 조용히 거짓말을 시작한다.
 */

import { bgm } from './audio/bgm'
import { audio } from './audio/engine'
import type { SoundName } from './audio/sounds'
import { createAccountEconomy, type OpenedEnvelopes } from './app/accountEconomy'
import { createAccountMagazines } from './app/accountMagazines'
import { mintAccountSeed } from './app/accountSeed'
import { createDailyFlow } from './app/dailyFlow'
import { createKitFlow } from './app/kitFlow'
import type { AppScreen } from './app/appMode'
import { installBgmUnlockGesture, reportFatalError, resolveCanvas, syncDocumentMeta } from './app/boot'
import { exposeDebugHandle } from './app/debugHooks'
import { createInputLayer } from './app/inputLayer'
import { createLobbyFlow } from './app/lobbyFlow'
import { createNetBoot } from './app/netBoot'
import { SETTINGS_ITEMS } from './app/settingsItems'
import { buildStage, type MatchStageHandle, type MatchStageRequest, type Stage, type StageRequest } from './app/stage'
import { createLoopbackMatch } from './app/stageMatch'
import type { StageSession } from './app/stageSession'
import {
  DEFAULT_BOT_LEVEL,
  FIRST_ATTEMPT,
  clearLocationQuery,
  resolveAttempt,
  resolveBotMatch,
  resolveDevTools,
  resolveFreeModeSeed,
  resolveLevelId,
  resolvePullSeq,
  resolveRoomCode,
  writeAttemptQuery,
  writeLevelQuery,
  writeMatchQuery,
  writeRoomQuery,
  writeSeedQuery,
  type BotMatchRequest,
} from './app/urlState'
import {
  DEFAULT_FLOOR_PLAN,
  getFloorPlan,
  parsePartyPlanId,
  partyPlan,
  partyPlanForSeats,
  type FloorPlan,
} from './core/floorPlan'
import { MAX_ROOM_SEATS } from './net/protocol'
import type { KitDef } from './game/economy/kits'
import { pinsForOutcome, shortByOf } from './game/economy/pins'
import { LEVELS, getLevel, levelSummary, type LevelDef } from './game/levels'
import {
  clearedPrefixCount,
  commitCoached,
  commitLevelResult,
  getLevelRecord,
  highestUnlockedLevelId,
  nextAttemptFor,
  loadProgress,
  type Progress,
} from './game/progress'
import { resolveRoundTuning } from './game/roundTuning'
import { playWithHaptics } from './game/sessionAudio'
import type { ResultPinsView, RoundOutcome } from './game/session'
import { TUNING, resolveTuning, type Tuning } from './game/tuning'
import { onLocaleChange, t } from './i18n'
import { preloadCatalogModels } from './render/meshFactory'
import { createScene, startRenderLoop } from './render/scene'
import { applyCosmetics } from './ui/cosmeticVars'
import { createEnvelopeScreen, type EnvelopeView } from './ui/envelope'
import { createLevelIntro } from './ui/levelIntro'
import { createLevelSelect, toLevelCards, type LevelBest } from './ui/levelSelect'
import { createMenu } from './ui/menu'
import { createPartyHub } from './ui/partyHub'
import { createLocker } from './ui/locker'
import { createSettingsMenu, isSettingsOpen } from './ui/settings'
import { createTitleScreen } from './ui/titleScreen'

/** 자유 모드의 평면도. 단계와 무관한 방이므로 가장 큰 것(아파트)을 쓴다. */
const FREE_MODE_PLAN: FloorPlan = DEFAULT_FLOOR_PLAN

/**
 * 개발 훅이 들고 있는 소리 기록의 길이.
 *
 * 배경 탭·자동 검증에서는 소리를 들을 수 없다. 그래서 "울렸는가"를 눈으로 볼 경로가 없으면
 * 효과→소리 배선은 검증할 방법이 아예 없다(`__weirdRoom.sounds()`).
 * 한 라운드의 사격이 대략 스무 발이라 그 두 배를 잡았다 - 라운드 하나가 통째로 남는다.
 */
const SOUND_LOG_MAX = 40

/* --- 조립 ----------------------------------------------------------------------- */

/** 조립 본체. 정리 함수를 돌려준다(HMR·테스트 페이지가 재조립할 때 필요하다). */
async function bootstrap(): Promise<() => void> {
  const search = window.location.search
  const canvas = resolveCanvas()

  /** 배치 모드를 세울 것인가. 이 값 하나가 이 앱에 모드가 둘인지 하나인지를 정한다. */
  const devTools = resolveDevTools(search)

  /*
   * 주소창이 정한 입구를 **부팅 첫 줄에서** 읽는다.
   *
   * 이 판단이 GLB를 기다린 뒤로 밀리면 타이틀도 그만큼 늦게 뜬다. 타이틀은 사람이 읽는
   * 동안 무거운 것을 받아 두는 화면이므로, 늦게 뜨는 타이틀은 그 시간을 통째로 잃는다.
   */
  const directLevelId = resolveLevelId(search)
  // 봇 판(`?match=bots`)이 자유 모드(`?seed=`)보다 먼저다 - 그 시드는 봇 판의 방이다(`urlState.resolveBotMatch`).
  const botMatch = resolveBotMatch(search)
  // 소켓 방(`?room=ABCD`, 로드맵 M6). 봇 판과 배타적이다 - 둘 다 "어느 판인가"라 하나만 이긴다(`urlState.deleteMatchKeys`).
  const directRoomCode = botMatch === null ? resolveRoomCode(search) : null
  const freeModeSeed = resolveFreeModeSeed(search)

  // index.html이 적어 둔 탭 제목·문서 언어는 스크립트 이전의 기본값이다. 지금 언어로 한 번 덮는다.
  syncDocumentMeta()

  /* --- 타이틀 --------------------------------------------------------------- */

  /*
   * **첫 화면이며, 이 파일에서 가장 먼저 서는 것이다.**
   *
   * 이유는 둘이고 둘 다 시점 문제다.
   *
   * 하나. 브라우저는 사용자 제스처 없이 AudioContext를 시작해 주지 않는다. 지금까지 그
   * 해제는 단계 시작 버튼에 얹혀 있어서(`startLevel`), 앱을 켜고 가장 오래 머무는 화면인
   * 단계 선택이 **언제나 무음**이었다. 이 문 하나가 첫 제스처를 보장한다.
   *
   * 둘. 사람이 이 화면을 읽는 시간은 어차피 흘러간다. 그동안 GLB 151종(`preloadCatalogModels`)과
   * 글꼴 넷이 도착하고, 아래 첫 화면 블록이 배경 방까지 미리 굽는다.
   * 그래서 타이틀은 **`await`보다 위**에 있어야 한다. 아래로 내리면 그 시간에 사람이 보는 것이
   * 검은 화면이고, 그러면 같은 대기가 선로드가 아니라 그냥 로딩이 된다.
   *
   * 이 화면에 로딩 표시를 붙이지 않는 이유도 거기 있다 - 선로드는 **최적화지 전제가 아니다.**
   * 실패해도(전량 절차적 폴백) 게임은 그대로 진행되고, 아직 안 받았으면 누른 뒤에 기다리면 된다.
   * 캐시가 더운 상태의 실측이 100ms대라(GLB 201개 fetch+파싱) 대개 다 받은 뒤에 손이 온다.
   */

  /** 배선이 끝나 시작을 받을 수 있는가. 타이틀은 GLB보다 먼저 뜨므로 그 사이가 실제로 있다. */
  let bootReady = false
  /** 준비되기 전에 눌렸다. 그 한 번을 기억해 두었다가 준비되는 즉시 이어 간다. */
  let startRequested = false

  const title = createTitleScreen({
    mount: document.body,
    onStart: () => {
      /*
       * **여기는 사용자 제스처의 콜스택 안이다**(`titleScreen.ts`의 계약). 이 앱에서
       * AudioContext가 살아서 열리는 자리는 여기 하나뿐이고, 타이머나 `await` 뒤로 한 줄만
       * 밀려도 브라우저가 정지 상태의 컨텍스트를 준다. 그러면 화면은 멀쩡하고 소리만 없다.
       */
      audio.unlock()
      bgm.unlock()
      /*
       * 메뉴 곡. **밝은 풀**에서 뽑는다 - 여기는 관찰 중이 아니라서 빠른 곡이 어울리고,
       * 그 넷(라데츠키·엔터테이너·나흐트무지크·터키 행진곡)은 플레이 풀에서 빠져 있어 논다.
       * 판이 서면 세션이 `bgm.start('play')`로 갈아 끼우므로 메뉴 곡이 판까지 따라가지 않는다
       * (`session.ts`의 `startBgm`. `start()`는 뽑기 전에 스스로 앞 곡을 끈다).
       * 배경음이 음소거면 곡만 정해 두고 노드를 하나도 만들지 않는다.
       *
       * `ensurePool`인 이유는 곧바로 이어지는 `showMenu()`가 같은 줄을 부르기 때문이다 -
       * 두 곳이 다 "이 풀의 곡이 아니면 뽑는다"여야 방금 튼 곡이 다음 화면에서 끊기지 않는다.
       */
      bgm.ensurePool('bright')

      startRequested = true
      /*
       * 아직 배선이 안 섰으면 **화면은 그대로 둔다.** 여기서 접으면 GLB가 남은 만큼 검은
       * 화면이 남는데, 그건 누른 사람에게 "고장"으로 읽힌다. 버튼을 잠그지 않는 것이 규약이라
       * (로딩 때문에 못 누르는 버튼은 이 화면을 로딩 화면으로 만든다) 기다림은 누른 뒤에 진다.
       * 그동안에도 곡은 이미 흐르므로 눌렀다는 사실 자체는 소리로 확인된다.
       */
      if (bootReady) enterFromTitle()
    },
  })

  /*
   * `?level=`·`?match=bots`·`?seed=` 직행에는 문을 세우지 않는다. 도구 경로에 마찰을 늘리면 안 되고,
   * 그 경로의 오디오는 창 단위 첫 제스처(`installBgmUnlockGesture`)가 이미 덮는다.
   */
  const usesTitle = directLevelId === null && botMatch === null && directRoomCode === null && freeModeSeed === null
  if (usesTitle) title.show()

  /*
   * **이 앱의 WebGL 컨텍스트는 이 한 줄이 전부다.** 무대는 몇 번이든 다시 서지만 여기로
   * 돌아오지 않는다(`app/stage.ts` 머리말과 `app/stage.test.ts`).
   * 어떤 무대든 절두체는 곧 다시 맞춰지므로, 여기서는 씬을 세울 최소한의 크기만 준다.
   */
  const ctx = createScene(canvas, getFloorPlan('room').bounds)

  // GLB를 먼저 읽는다. 소품 뷰가 만들어진 **뒤**에 템플릿이 도착하면
  // 그 방은 절차적 형상으로 굳고, 새로고침 전까지 카툰 룩을 볼 수 없다.
  // 실패는 치명적이지 않다(전량 절차적 폴백) - 대신 조용히 넘기지 않고 콘솔에 남긴다.
  // **타이틀이 이미 떠 있는 동안 진행된다**(위 절). 실패해도 [시작하기]는 막히지 않는다.
  const models = await preloadCatalogModels()
  if (models.failed.length > 0) {
    console.warn(
      `[main] GLB ${models.failed.length}종을 읽지 못해 절차적 형상으로 대체한다.`,
      models.failed,
    )
  }

  /**
   * 판이 서면 한 번 띄울 토스트. **지금은 띄울 데가 없다** - 토스트는 게임 HUD의 것이고
   * 그 HUD는 무대와 함께 태어나는데, 여기는 아직 무대가 없는 시점이다(타이틀만 떠 있다).
   *
   * 콘솔에만 남기고 끝내지 않는 이유는 증상이 조용하기 때문이다. 절차적 폴백은 게임을
   * 멈추지 않고 **방이 조금 달라 보일 뿐**이라, 화면이 말하지 않으면 사람은 그것을
   * 이 게임의 원래 모습으로 받아들인다. 그러면 제보가 오지 않는다.
   *
   * 한 번 띄우고 비운다(`flushPendingNotice`). 매 판 뜨면 그건 경고가 아니라 소음이다.
   */
  let pendingNotice: string | null = models.failed.length > 0 ? t('error.assetsFallback') : null

  /*
   * 계정 시드 발급기를 넘긴다. 저장에 시드가 없을 때(첫 실행 · v2 저장)만 불리고, 엔트로피를 쓰는 자리는
   * `app/accountSeed.ts` 하나다. 저장이 막힌 브라우저에서는 매 세션 새 시드가 나오지만 그때는 어차피
   * 봉투 회차도 저장되지 않으므로 잃는 것이 없다.
   */
  let progress: Progress = loadProgress(undefined, mintAccountSeed)
  let stage: Stage | null = null

  /**
   * 여럿이 첫 바꾸기 안내(`ui/changerCoach.ts`)의 문 둘. 무대가 저장소를 모르게 하려고 여기서 진행을 읽고 쓴다
   * (`accountMagazines`와 같은 방향). **매번 `progress`를 다시 읽는 함수**라 한 판의 1라운드에서 마친 안내가
   * 2라운드에 다시 서지 않는다. 저장이 막힌 브라우저에서는 다음 실행에 한 번 더 뜬다 - 편의가 콘텐츠를 죽이지 않는다.
   */
  const needsCoach = (): boolean => !progress.coached
  function markCoached(): void {
    if (progress.coached) return
    progress = commitCoached(progress).progress
  }

  /** 봇 판의 로비 자동 진행 구독(`lobbyFlow.autopilot`). 무대가 갈리면 여기서 푼다 - 소켓 방에는 없다(사람이 누른다). */
  let stopAutopilot: (() => void) | null = null
  // 문이 먼저다. `?level=`·`?seed=` 직행은 아래 첫 화면 블록에서 곧바로 `play`로 넘긴다.
  let screen: AppScreen = 'title'

  /* --- 오디오 ---------------------------------------------------------------- */

  /*
   * **AudioContext는 첫 사용자 제스처 안에서만 시작된다.** 그 전에는 엔진이 컨텍스트를
   * 만들지도 않으므로(`audio/engine.ts`) 페이지를 여는 것만으로는 어떤 소리도 나지 않고,
   * 콘솔에 정책 위반 경고가 쌓이지도 않는다.
   *
   * 여기서 잡는 것은 **어떤 제스처든 첫 번째 하나**다(pointerdown·touchend·keydown).
   *
   * 사람이 걸어 들어오는 길에서는 이것이 쓰일 일이 없다 - 타이틀의 [시작하기]가 첫 제스처를
   * 보장하고 그 콜스택 안에서 이미 열려 있다. **이 두 줄이 지키는 것은 타이틀을 세우지 않는
   * 입구 둘**이다: `?level=`·`?seed=` 직행. 그 경로에는 제스처가 하나도 없이 판이 서므로,
   * 여기가 없으면 첫 탭이 올 때까지 컨텍스트가 정지 상태로 남아 그 판이 통째로 무음이다.
   * (예전에는 `startLevel`·`startFreeRound`에서도 한 번씩 더 불렀다. 타이틀이 선 뒤로 그
   * 방어는 중복이 됐고, 직행 경로에서는 애초에 제스처가 아니라 정지된 컨텍스트만 만들었다.)
   */
  const stopUnlockGesture = audio.installUnlockGesture()
  // 배경음은 별도 재생기라 해제도 따로다(`app/boot.ts`의 `installBgmUnlockGesture` 주석).
  const stopBgmGesture = installBgmUnlockGesture()

  /**
   * 소리 기록. **개발 빌드에서만 쌓는다**(`import.meta.env.DEV` 가지치기).
   *
   * 배경 탭·임베드에서는 소리를 들을 수 없으므로 효과→소리 배선을 확인할 다른 방법이 없다.
   * `__weirdRoom.sounds()`로 꺼내 "정답에서 hit이 반음씩 올라갔는가"까지 볼 수 있다.
   */
  const soundLog: { name: SoundName; step: number }[] = []

  function recordSound(name: SoundName, step: number): void {
    if (!import.meta.env.DEV) return
    soundLog.push({ name, step })
    if (soundLog.length > SOUND_LOG_MAX) soundLog.shift()
  }

  /* --- 설정 ------------------------------------------------------------------ */

  /**
   * 톱니바퀴 하나. **언제나 떠 있다** - 파티게임은 방송·공공장소에서 켜지고, 소리를 끄고
   * 싶어지는 순간은 화면과 무관하게 온다(자리 근거는 `gameHud.css`의 `.gh-gear`).
   *
   * 이 버튼이 언어 국기와 음소거 스피커를 대신한다. 셋을 각자 세우면 화면 오른쪽 위가
   * 스위치밭이 되고, 관찰 게임에서 UI가 덮은 면적은 곧 볼 수 없는 방의 넓이다(원칙 2).
   *
   * 항목 표는 `app/settingsItems.ts`에 있다 - 이 파일이 아는 것은 "톱니가 하나 서 있다"까지다.
   */
  const settings = createSettingsMenu({ mount: document.body, items: SETTINGS_ITEMS })

  /**
   * 주소창이 지정한 시도 번호. **처음 세우는 한 판에만 쓰이고 소진된다.**
   *
   * 지정이 없으면 `null`이다 - 그때 무엇을 쓸지는 부르는 쪽이 안다(단계는 저장된 판 수, 자유 모드는 첫 시도).
   * 이후의 시도 번호는 세션이 스스로 센다(재도전할 때마다 +1). 소진시키지 않으면
   * 다음 단계로 넘어가거나 새 방을 뽑았을 때도 `attempt=3`이 따라붙어, 주소창이 말하는 값과
   * 실제로 도는 값이 갈린다 - 재현 도구가 재현을 방해하는 상태다.
   */
  let pendingAttempt = resolveAttempt(search)

  function takeStartAttempt(): number | null {
    const value = pendingAttempt
    pendingAttempt = null
    return value
  }

  /**
   * 시도 번호를 주소창에 비춘다 - **개발 도구가 켜져 있을 때만**. 아니면 오히려 지운다.
   *
   * 자동으로 적히면 재현 도구가 그대로 반복 도구가 된다. `attempt=2`가 붙은 주소가 남으면 새로고침이 곧
   * "그 회차를 다시 부르기"이고, 고정된 방에 같은 정답표가 몇 번이든 나온다 - 핀은 판마다 붙으므로
   * (로드맵 3.1절) 그 반복은 그대로 핀 복사기다(사용자 신고).
   *
   * 사람이 **직접 적은** `?attempt=`는 그대로 존중한다(`takeStartAttempt`가 한 판에 쓴다) - 재현 의도가
   * 분명한 입력이라서다. 다만 쓰고 나면 지운다: 남겨 두면 그 뒤 모든 새로고침이 같은 판이 된다.
   */
  function mirrorAttempt(attempt: number): void {
    writeAttemptQuery(devTools ? attempt : FIRST_ATTEMPT)
  }

  /**
   * 계정 탄창. 진행 저장은 이 파일이 쥐고 있으므로 읽고 쓰는 두 함수만 넘긴다
   * (`app/accountMagazines.ts` 머리말 - `?mags=` 강제 중에는 저장을 건드리지 않는다).
   */
  const accountMagazines = createAccountMagazines({
    search,
    getProgress: () => progress,
    setProgress: (next) => {
      progress = next
    },
  })

  /**
   * 계정 핀·봉투·사물함. 탄창과 같은 모양으로 읽고 쓰는 두 함수만 넘기고, 탄창 지급은 위 모듈의 것을 빌린다 -
   * 봉투가 준 탄창이 계정에 들어가는 길은 `accountMagazines.grant` 하나여야 한다(`'envelope'`이 첫 실제 지급 경로다).
   */
  const accountEconomy = createAccountEconomy({
    search,
    getProgress: () => progress,
    setProgress: (next) => {
      progress = next
    },
    grantMagazines: (count) => accountMagazines.grant(count, 'envelope'),
    magazineCount: accountMagazines.count,
  })

  /**
   * 매일의 방(로드맵 M9). **오늘이 며칠인지는 이 줄에서 한 번만 읽힌다** - 화면마다 다시 읽으면
   * 자정을 넘긴 한 판이 두 날짜에 걸친다(`app/dateKey.ts`·`app/dailyFlow.ts` 머리말).
   */
  const dailyFlow = createDailyFlow({
    search,
    getProgress: () => progress,
    setProgress: (next) => {
      progress = next
    },
    earnPins: accountEconomy.earn,
  })

  /**
   * 준비물 3택1(로드맵 M9). 제시의 시드가 `(계정, 단계, 판 수)`라 셋을 조회 함수로만 넘긴다 -
   * 진행 저장은 이 파일이 쥐고 있고, 그 값이 판마다 바뀐다(`app/kitFlow.ts` 머리말).
   */
  const kitFlow = createKitFlow({
    accountSeed: () => progress.accountSeed,
    inventory: accountEconomy.inventory,
    playsOf: (levelId) => getLevelRecord(progress, levelId)?.plays ?? 0,
    useKit: accountEconomy.useKit,
  })

  /*
   * 끼운 꾸밈을 문서 뿌리에 심는다(로드맵 M9 - `ui/cosmeticVars.ts`). **CSS 변수 몇 개가 전부**라
   * 게임 수치도 HUD 뷰도 한 글자 바뀌지 않는다. 뿌리에 심는 이유는 무대가 판마다 다시 서기 때문이다 -
   * HUD에 심으면 다음 무대에서 사라진다.
   */
  function refreshCosmetics(): void {
    applyCosmetics(document.documentElement, accountEconomy.inventory().equipped)
  }

  refreshCosmetics()

  /**
   * 마지막으로 끝난 판의 핀 줄. `saveOutcome`이 채우고 결과 화면이 `resultPins`로 읽는다(`sessionResult.ts`의 순서 -
   * `report`가 `show`보다 먼저다). 판이 새로 서면 비운다 - 오류 경로로 뜬 결과 화면이 지난 판의 핀을 말하면 안 된다.
   */
  let lastRoundPins: ResultPinsView | null = null

  /** `?pull=seq`. 첫 선택 화면에서 한 번 소진된다(`showSelect`) - 그 뒤로 선택 화면에 올 때마다 다시 뜨면 도구가 방해가 된다. */
  let pendingPull: number | null = resolvePullSeq(search)

  /* --- 진행도 조회 ---------------------------------------------------------- */

  /*
   * **해금 규칙을 여기서 다시 쓰지 않는다.** 한때 이 자리에 `clearedPrefix`와 `currentLevelId`가
   * 있었고, 둘은 `progress.ts`의 `clearedPrefixCount`·`highestUnlockedLevelId`와 정확히 같은
   * 사실을 다른 코드로 세고 있었다. 같은 규칙이 두 곳에 있으면 언젠가 한쪽만 고쳐지고,
   * 그때 증상은 "깼는데 다음 단계가 안 열린다"라 재현조차 어렵다(파일 상단: 해금 규칙은
   * `game/progress.ts`에 있다).
   */

  /**
   * 단계 하나의 최고 기록. 저장 포맷을 화면의 계약(`LevelBest`)으로 옮기는 유일한 자리다.
   *
   * 한 번도 안 해 본 단계는 `null`이다 - 0점을 기록으로 내밀면 "아직 기록 없음"이라는 말이
   * 화면에서 사라진다. 나머지 셋은 없으면 **비운 채로** 넘긴다. 0으로 메우면 "0/0"이나
   * "0:00"이 되고, 그건 존재한 적 없는 판을 기록표에 적는 일이다(`ui/levelSelect.ts`).
   */
  function levelBestOf(levelId: number): LevelBest | null {
    const record = getLevelRecord(progress, levelId)
    if (record === undefined || record.plays === 0) return null

    return {
      score: record.bestScore,
      foundCount: record.bestFound ?? undefined,
      changeCount: record.bestChangeCount ?? undefined,
      elapsedMs: record.bestTimeMs ?? undefined,
    }
  }

  /**
   * 단계의 최종 튜닝. 순서는 **단계 → 준비물 → URL**이고 그 순서의 원본은 `game/roundTuning.ts`다
   * (로드맵 M9). **URL이 단계도 준비물도 이긴다** - 근거는 `app/urlState.ts`의 URL 표이고,
   * `?changes=1`로 단계를 검사할 수 없으면 도구가 아니다.
   *
   * `kit`을 인자로 받는 이유는 자리마다 답이 다르기 때문이다 - 배경 무대는 아직 아무도 고르지 않았고(`null`),
   * 안내 화면은 지금 고른 것을 미리 보여 주며, 라운드는 **소비한** 것을 얹는다.
   */
  function tuningFor(level: LevelDef, kit: KitDef | null): Tuning {
    return resolveRoundTuning(level, kit, search)
  }

  /* --- 오버레이 화면 -------------------------------------------------------- */

  /*
   * 두 화면은 **만들어질 때** 문구를 굽는다. 제목·머리 태그·규칙 라벨·버튼 이름은
   * 생성자에서 한 번 `t()`를 부르고 그대로 굳는다(`show`는 회차 줄만 다시 그린다).
   * 언어가 바뀌었을 때 다시 그리기만으로는 그 굳은 문구가 옛 언어로 남으므로,
   * 아래 `rebuildOverlays`가 **통째로 다시 만든다.** 그래서 `const`가 아니다.
   *
   * 여기서 두 화면에 `onLocaleChange`를 심지 않는 이유는 소유권이다 - 이 두 화면의 수명은
   * 이 파일이 쥐고 있고, 만드는 쪽과 버리는 쪽이 갈리면 한쪽만 남는 상태가 생긴다.
   */
  function buildLevelSelect(): ReturnType<typeof createLevelSelect> {
    return createLevelSelect({
      mount: document.body,
      handlers: {
        onSelect: (levelId) => showIntro(levelId),
        // 뒤로는 언제나 메뉴다(로드맵 M13). 이 화면에 오는 길은 메뉴의 "혼자 연습" 카드 하나뿐이다.
        onBack: () => showMenu(),
      },
    })
  }

  /**
   * 메뉴(로드맵 M13). [시작하기] 뒤에 서는 화면이고 여섯 갈래가 전부 여기서 갈린다.
   *
   * 카드가 여는 곳은 넷이 이미 있던 문이다(매일의 방 · 단계 선택 · 봉투 · 사물함). 새로 생긴 것은
   * "모두의 방"이 로비가 아니라 **한 층 더**(`showParty`)를 여는 것뿐이다 - 공개·비공개·겨루기가
   * 갈리는 자리라 카드 하나로는 말할 수 없다(M14).
   *
   * 설정 카드는 톱니와 **같은 패널**을 연다(M13 규칙 3). 두 번째 설정 화면을 만들면 값이 두 곳에 산다.
   */
  function buildMenu(): ReturnType<typeof createMenu> {
    return createMenu({
      mount: document.body,
      handlers: {
        // 안내 화면을 거치지 않는다 - 고를 것도 챙길 것도 없는 한 판이다(로드맵 M9).
        onDaily: () => startDaily(),
        onSolo: () => showSelect(),
        onParty: () => showParty(),
        onEnvelope: () => openEnvelopes(1),
        onLocker: () => showLocker(),
        onSettings: () => settings.open(),
      },
      party: { seatsMax: MAX_ROOM_SEATS },
    })
  }

  /**
   * 모두의 방(로드맵 M13 · M14). 지금 서 있는 카드는 둘이다 - 친구와 한 판(로비)과 겨루기(잠긴 판).
   *
   * 카드가 약속하는 것 둘 - 방 정원과 이웃의 성격. 이웃 **수**는 방 안에서 정해지므로 카드가 말하지 않는다.
   * 난이도가 여기 기본값과 같은 이유는 서버가 이웃을 그 값으로 고정하기 때문이다(M6a).
   */
  function buildPartyHub(): ReturnType<typeof createPartyHub> {
    return createPartyHub({
      mount: document.body,
      handlers: {
        /*
         * 친구와 한 판 → 로비(로드맵 M6). M2~M5에서는 이 문이 곧바로 봇 판을 열었다 - 이제는 방을 만들거나 번호로 들어가는
         * 문이고, 이웃은 그 방 안에서 넣는다. 봇 판(같은 탭의 루프백)은 도구로 남는다: `?match=bots`와 개발 훅.
         */
        /*
         * 아무나 한 판 → 열린 방 목록(M14). 여기서 고르는 것도 [새 방 열기]도 결국 같은 `hello`로 간다
         * (`app/lobbyFlow.ts` - 목록 전용 진입로를 만들지 않는다). 다른 것은 새 방의 공개 여부 하나다.
         */
        onPublic: () => lobbyFlow.openBrowser(),
        onPrivate: () => openLobby(null),
        onBack: () => showMenu(),
      },
      seatsMax: MAX_ROOM_SEATS,
      neighborLevel: DEFAULT_BOT_LEVEL,
    })
  }

  /*
   * 여기 있던 `nextPartySeed`가 사라졌다(M6). 카드가 열던 것이 봇 판일 때는 카드가 방 시드를 정해야 했지만, 이제 카드는
   * 로비를 열고 방 시드는 서버가 발급한다(`welcome.roomCode`). 봇 판의 시드는 주소창(`?seed=`)과 개발 훅이 정한다.
   */

  function buildLevelIntro(): ReturnType<typeof createLevelIntro> {
    return createLevelIntro({
      mount: document.body,
      handlers: {
        onStart: () => {
          if (introLevelId !== null) startLevel(introLevelId)
        },
        onBack: () => showSelect(),
        /*
         * 준비물을 골랐다(로드맵 M9). 화면을 **다시 그린다** - 고른 것에 따라 규칙 칸의 시간·탄환이 바뀌므로
         * (`showIntro`), 카드만 칠하면 숫자와 선택이 어긋난 화면이 남는다.
         */
        onPickKit: (id) => {
          kitFlow.pick(id)
          if (introLevelId !== null) showIntro(introLevelId)
        },
      },
    })
  }

  let menu = buildMenu()
  let partyHub = buildPartyHub()
  let levelSelect = buildLevelSelect()
  let levelIntro = buildLevelIntro()

  /*
   * 봉투·사물함. 둘은 스스로 언어 전환을 구독하므로(`onLocaleChange`) `rebuildOverlays`에 들지 않는다 -
   * 생성 시점에 문구를 굽지 않고 `show`마다 다시 칠하기 때문이다. 소리는 이름만 받아 엔진에 넘긴다.
   */
  const envelopeScreen = createEnvelopeScreen({
    mount: document.body,
    handlers: {
      onOpen: (count) => openEnvelopes(count),
      onClose: () => showMenu(),
    },
    /*
     * 소리와 진동이 **같은 문 하나**를 지난다(`playWithHaptics`, `game/sessionAudio.ts`). 여기서
     * `audio.play(sound(name))`만 부르면 SR·SSR의 진동이 조용히 빠지고, 그 누락은 안드로이드에서만 드러난다.
     */
    playSound: (name) => {
      playWithHaptics(name)
    },
  })

  const locker = createLocker({
    mount: document.body,
    handlers: {
      onEquipLook: (id) => {
        accountEconomy.equipLook(id)
        // 갈아 끼운 것이 **다음 판이 아니라 지금** 붙는다. 미리보기가 없는 화면이라 이 즉시성이 유일한 확인 수단이다.
        refreshCosmetics()
        showLocker()
      },
      onEquipNickname: (id) => {
        accountEconomy.equipNickname(id)
        refreshCosmetics()
        showLocker()
      },
      onBack: () => showMenu(),
    },
  })

  /* --- 여럿이 방 ------------------------------------------------------------ */

  /**
   * 방 서버와 말하는 문(로드맵 M6). 주소는 여기서 한 번 정해지고(`VITE_WS_URL` 없으면 같은 호스트의 `/ws`), 재접속 토큰은
   * 이 탭의 `sessionStorage`에 산다 - 저장소를 아는 파일이 하나여야 새로고침이 다른 좌석에 앉지 않는다(`app/netBoot.ts` 머리말).
   */
  const net = createNetBoot()

  /**
   * 로비 흐름. 이 파일이 넘기는 것은 **화면 전환과 무대 교체**뿐이고, 소켓·좌석·[준비]·[시작]은 전부 그쪽이 쥔다
   * (`app/lobbyFlow.ts`). 봇 판의 자동 진행(`autopilot`)도 같은 파일에 있다 - 사람 로비와 같은 뷰를 읽어야 봇 판이
   * 소켓 방의 검증 수단으로 남는다.
   */
  const lobbyFlow = createLobbyFlow({
    mount: document.body,
    net,
    mountMatch: (request, active, templateId) => mountRoomMatch(request, active, templateId),
    onLobby: () => showLobby(),
    onPlay: () => enterRoomPlay(),
    onLeave: () => showMenu(),
    // 방 목록(M14). 로비와 같은 무게의 화면이라 같은 자리를 쓴다 - 판이 돌지 않고 손이 캔버스를 만지면 안 된다.
    onBrowse: () => showBrowse(),
    onBrowseBack: () => showParty(),
    writeRoom: writeRoomQuery,
  })

  /**
   * 서버 방의 평면도. **고르는 것이 아니라 같은 것을 굽는다**(로드맵 M11 - M6의 '평면도는 고르지 않고 대조한다'를 뒤집는다).
   *
   * 서버의 방은 이제 좌석 수만큼 구획을 가지며(`server/src/deps.ts`의 `partyPlanForSeats`) 그 사실이 와이어에 오는 것은
   * `RoomState.templateId`(`party-<n>`) 하나뿐이다. 그 문자열에서 좌석 수를 되읽어 **같은 함수**로 다시 구우면 양쪽의
   * 벽·문·창이 정확히 같아진다(`core/partyPlan`은 난수도 시계도 읽지 않는다).
   *
   * 어긋난 채로 무대를 세우면 셸·조명·격자·카메라 절두체가 서버의 방과 다른 크기로 서서 소품이 벽을 뚫는데,
   * 그 증상은 "가끔 방이 이상하다"로만 보여 원인을 찾기가 거의 불가능하다. 그래서 모르는 id는 경고를 남긴다.
   * 로비에서는 방이 아직 없어 `null`이 오고, 그때는 로비가 좌석 수로 지어 준 id가 대신 들어온다(`app/lobbyFlow.ts`).
   */
  function planForRoomTemplate(templateId: string | null): FloorPlan {
    const seats = templateId === null ? null : parsePartyPlanId(templateId)
    if (seats !== null) return partyPlan(seats)
    if (templateId !== null && templateId !== FREE_MODE_PLAN.id) {
      console.warn(`[main] 서버 방의 평면도 '${templateId}'를 모른다 - '${FREE_MODE_PLAN.id}'로 세운다.`)
    }
    return FREE_MODE_PLAN
  }

  /**
   * `welcome`이 왔다 - 그 소켓 위에 매치 무대를 세운다. 로비 뒤에서는 잠든 채(`active: false`)이고, 진행 중인 방으로 돌아왔으면
   * 깨어난 채다. 시드 자리에 방 번호가 들어가는 이유는 소켓 방이 방 시드를 모르기 때문이다(원본은 서버에 있다 - `MatchStageRequest`).
   *
   * 탄창은 넘기지 않는다. 서버가 매치 참가자를 탄창 0으로 앉히고(계정 값을 검증할 길이 없다) 그 편이 "뽑기 많이 한 사람이 이기지
   * 않는다"와도 맞다 - 그래도 `onMagazineUsed`는 봇 판과 같은 배선으로 둔다. 예외를 하나 만들면 다음 사람이 그 예외를 먼저 읽는다.
   */
  function mountRoomMatch(request: MatchStageRequest, active: boolean, templateId: string | null): MatchStageHandle {
    setScreen(active ? 'play' : 'lobby')
    swapStage({
      plan: planForRoomTemplate(templateId),
      tuning: resolveTuning(search),
      seed: request.roomCode,
      match: request,
      onMagazineUsed: accountMagazines.spend,
      needsCoach,
      onCoached: markCoached,
      onSound: recordSound,
      startActive: active,
      withEditor: devTools,
      // 결과 화면의 [나가기]. 방을 떠나는 것이므로 좌석 기억도 함께 버린다 - 새로고침이 끝난 방으로 되돌아가면 안 된다.
      onExitToSelect: () => {
        net.forgetSeat()
        lobbyFlow.leave()
        showMenu()
      },
    })
    const handle = stage?.match
    // `match`를 넘겨 세운 무대에는 반드시 손잡이가 있다(`buildStage`). 없으면 조립이 깨진 것이라 조용히 넘기면 안 된다.
    if (handle === null || handle === undefined) throw new Error('[main] 여럿이 무대를 세웠는데 매치 손잡이가 없다')
    return handle
  }

  /** 로비 화면(폼 또는 방)이 앞에 선다. 무대는 뒤에서 잠들어 있을 수 있다 - 손이 캔버스를 만지면 안 되므로 `select`와 같은 편이다. */
  function showLobby(): void {
    setScreen('lobby')
    title.hide()
    hidePlates()
    stage?.session.setActive(false)
    bgm.ensurePool('bright')
  }

  /** 로비가 접히고 판이 돈다. 무대는 이미 서 있다(`mountRoomMatch`) - 여기서는 깨우기만 한다. */
  function enterRoomPlay(): void {
    setScreen('play')
    hidePlates()
    stage?.session.setActive(true)
  }

  /** "여럿이" 카드와 `?room=CODE`가 여는 문. 좌석 기억이 있으면 곧바로 그 자리로, 없으면 이름을 적는 폼이다. */
  function openLobby(code: string | null): void {
    if (code !== null && lobbyFlow.resume(code)) return
    lobbyFlow.openForm(code)
  }

  /*
   * 언어 토글(국기 버튼)은 사라졌다. 언어는 이제 설정 패널의 한 줄이다(`app/settingsItems.ts`).
   *
   * 같은 값을 바꾸는 입구가 화면에 둘 있으면 그 자체가 스위치밭이고, 그 둘이 하필 같은
   * 구석에 붙는다. 대신 잃은 것도 적어 둔다 - 선택 화면에서 언어를 바꾸는 데 탭이 한 번 더 든다.
   * 언어는 "한 번 정하면 다시 만지지 않는 값"이라 그 한 번을 내주고 구석을 비웠다.
   */

  /** 안내 화면이 지금 가리키고 있는 단계. 안내는 상태를 갖지 않으므로 여기서 들고 있는다. */
  let introLevelId: number | null = null

  /**
   * 화면을 옮긴다. 톱니바퀴의 명도가 여기에 딸려 있다 - 라운드 중에는 흐려져야 하고
   * (수색 중 구석에서 또렷한 아이콘은 방을 훑는 눈을 끌어당긴다), 그 판단의 근거는
   * "지금 판이 도는가" 하나뿐이라 화면 전환과 같은 줄에 둔다.
   */
  function setScreen(next: AppScreen): void {
    screen = next
    settings.setQuiet(next === 'play')
  }

  /**
   * 전면 자막판을 전부 접는다. **화면을 옮기는 함수마다 여섯 줄을 적지 않는다.**
   *
   * 판이 넷일 때는 자리마다 필요한 것만 접었는데, M13에서 여섯이 되면서 그 방식이 깨졌다 -
   * 새 판이 하나 늘 때마다 아홉 자리를 전부 고쳐야 하고, 하나를 빠뜨리면 **그 판만 남아**
   * 다음 화면 위에 떠 있는다. 접는 것은 싼 일이고(이미 접힌 판은 `hidden = true`가 두 번 될 뿐이다),
   * 곧바로 자기 판을 펴는 함수(`showEnvelope`·`showIntro`)는 이 줄 다음에 펴므로 순서가 이긴다.
   */
  function hidePlates(): void {
    menu.hide()
    partyHub.hide()
    levelSelect.hide()
    levelIntro.hide()
    envelopeScreen.hide()
    locker.hide()
  }

  /* --- 화면 전환 ------------------------------------------------------------ */

  /**
   * 부팅 때 밀어 둔 경고를 **판이 실제로 도는 무대에서** 한 번 띄운다.
   *
   * 잠든 배경 무대(선택 화면 뒤)에 띄우지 않는 이유는 HUD가 접혀 있어서다 - 거기서 부르면
   * 아무도 못 본 채 소진되고, 그건 안 띄운 것보다 나쁘다(띄웠다고 믿게 된다).
   */
  function flushPendingNotice(session: StageSession): void {
    if (pendingNotice === null || !session.isActive()) return
    session.showNotice(pendingNotice, 'warn')
    pendingNotice = null
  }

  /**
   * 무대를 갈아 끼운다. **먼저 버리고 나중에 세운다.**
   *
   * 순서를 뒤집으면 한순간 두 무대가 같은 씬에 겹치고, 새 세션이 올린 소품을 옛 무대의
   * 정리 경로가 프록시째로 걷어 간다. 사이 구간은 프레임을 돌리지 않으므로 화면에 비지 않는다.
   */
  function swapStage(request: StageRequest): void {
    // 앞 무대의 로비 자동 진행(봇 판). 로비를 벗어나면 스스로 풀리지만, 로비에서 무대가 갈리면 그 구독만 남는다.
    stopAutopilot?.()
    stopAutopilot = null
    stage?.dispose()
    stage = null
    // 무대가 바뀌면 손이 하던 일도 초기화된다. 배치 모드를 유지하면 새 방의 에디터가 꺼진 채 뜬다.
    input.resetToGameMode()
    stage = buildStage(ctx, request)
    flushPendingNotice(stage.session)
    input.refresh()
  }

  /**
   * 선택 화면의 내용을 다시 그린다. **화면을 옮기지 않는다.**
   *
   * 전환(`showSelect`)에서 갈라낸 이유는 언어가 바뀌었을 때 필요한 것이 다시 그리기뿐이기
   * 때문이다. 전환을 통째로 다시 부르면 주소창을 지우고 배경 무대를 다시 판단하는 일까지
   * 딸려 오는데, 그건 "언어를 눌렀다"가 요구한 적 없는 부작용이다.
   */
  function renderSelect(): void {
    levelSelect.show(
      toLevelCards(
        LEVELS.map((entry) => levelSummary(entry, TUNING)),
        // `bestOf`를 빠뜨리면 모든 줄이 "아직 기록 없음"으로 그려진다 - 화면은 멀쩡한데
        // 열 판을 깬 사람의 기록이 통째로 없는 것으로 보이는, 조용히 틀리는 종류의 누락이다.
        { clearedCount: clearedPrefixCount(progress), bestOf: levelBestOf },
      ),
    )
  }

  /**
   * 메뉴의 내용을 다시 그린다. **화면을 옮기지 않는다**(`renderSelect`와 같은 이유).
   *
   * 카드가 말하는 것은 전부 저장에서 읽은 값이다 - "오늘 것을 했는가"는 `game/daily.ts`가,
   * "봉투를 열 수 있는가"는 `economy/pins.ts`가 판정하고 여기는 그 답만 옮긴다.
   */
  function renderMenu(): void {
    const cleared = clearedPrefixCount(progress)
    menu.show({
      daily: dailyFlow.cardView(),
      /*
       * "지금 할 단계"는 해금 규칙이 정한다(`highestUnlockedLevelId`). 전부 깼으면 `null`이고,
       * 그때 카드는 단계 번호 대신 졸업을 말한다 - 열 단계를 다 깬 사람에게 "다음은 10단계"는 거짓말이다.
       */
      solo: {
        nextLevel: cleared >= LEVELS.length ? null : highestUnlockedLevelId(progress),
        cleared,
        total: LEVELS.length,
      },
      account: {
        pins: accountEconomy.pins(),
        envelopePrice: accountEconomy.price().single,
        shortBy: shortByOf(accountEconomy.canOpen(1)),
      },
    })
  }

  /**
   * 선택·안내 화면을 버리고 새 언어로 다시 세운다. **화면은 그대로 둔다.**
   *
   * 다시 그리는 것으로 부족한 이유는 두 화면이 생성 시점에 문구를 굽기 때문이다
   * (`buildLevelSelect` 위의 주석). 제목만 옛 언어로 남은 화면은 "덜 번역된 앱"으로 읽히고,
   * 그건 언어를 바꾼 사람이 가장 먼저 보는 것이 그 제목이라 특히 나쁘다.
   *
   * 열 줄짜리 화면 하나를 다시 만드는 비용은 언어를 누른 순간에만 든다. 대신 얻는 것은
   * "이 화면의 모든 글자는 지금 언어다"라는, 예외가 없어서 확인할 필요도 없는 규칙이다.
   */
  function rebuildOverlays(): void {
    const wasIntroLevelId = introLevelId

    menu.dispose()
    partyHub.dispose()
    levelSelect.dispose()
    levelIntro.dispose()
    menu = buildMenu()
    partyHub = buildPartyHub()
    levelSelect = buildLevelSelect()
    levelIntro = buildLevelIntro()

    // 새로 만든 화면은 전부 접힌 채로 선다. 지금 떠 있던 것만 도로 편다.
    if (screen === 'menu') renderMenu()
    else if (screen === 'select') renderSelect()
    else if (screen === 'party') partyHub.show()
    else if (screen === 'intro' && wasIntroLevelId !== null) showIntro(wasIntroLevelId)
  }

  /**
   * "지금 할 단계"의 방을 **멈춘 채로** 배경에 세운다. 화면도 오버레이도 옮기지 않는다.
   *
   * 선택 화면과 타이틀이 같은 배경을 쓰기 때문에 갈라 두었다. 타이틀이 떠 있는 동안 이걸
   * 미리 돌려 두면 [시작하기] 다음에 방을 굽는 시간이 없어진다 - 사람이 화면을 읽는 시간에
   * 붙는 일이라 공짜다. 두 번 불려도 같은 단계면 다시 세우지 않는다.
   */
  function ensureBackgroundStage(): void {
    // "지금 할 단계"는 해금 규칙이 정한다. 그 규칙의 원본은 `game/progress.ts` 하나다.
    const level = getLevel(highestUnlockedLevelId(progress))
    // 단계 데이터가 비어 있을 수 없지만(검증 대상), 배경 하나 때문에 앱이 죽지는 않게 한다.
    if (level !== undefined && (stage === null || stage.levelId !== level.id)) {
      swapStage({
        plan: getFloorPlan(level.planSize),
        // 배경 방에는 준비물이 얹히지 않는다. 라운드가 돌지 않으므로 얹을 이유가 없고, 얹으면 고르기 전에 이미 쓴 것이 된다.
        tuning: tuningFor(level, null),
        seed: level.seed,
        targetObjectCount: level.objectTarget,
        magazines: accountMagazines.count(),
        /*
         * 배경에도 `level`을 준다. 라운드를 돌리지 않으므로(`startActive: false`)
         * 결과 화면도 진행 저장도 이 무대에서는 열리지 않지만, **`stage.levelId`가 채워져야**
         * 위의 "이미 그 방이면 다시 세우지 않는다" 판단이 성립한다.
         * 비워 두면 선택 화면에 들어올 때마다 같은 방을 처음부터 다시 굽는다.
         */
        level,
        startActive: false,
        withEditor: devTools,
      })
    }
    stage?.session.setActive(false)
  }

  /**
   * 메뉴로. 배경에는 "지금 할 단계"의 방을 **멈춘 채로** 세워 둔다(단계 선택과 같은 배경이다).
   *
   * 뒤로 가는 길은 전부 여기로 모인다(로드맵 M13) - 단계 선택 · 모두의 방 · 봉투 · 사물함 ·
   * 결과 화면의 [나가기]. 그 대신 **루프에는 끼지 않는다**(M13 규칙 1): 결과 화면의 [한 판 더]는
   * 세션 안에서 라운드만 다시 돌리므로 이 함수를 지나지 않는다. 메뉴 한 겹은 콜드 스타트에만 붙는다.
   */
  function showMenu(): void {
    setScreen('menu')
    introLevelId = null
    kitFlow.reset()
    hidePlates()
    clearLocationQuery()

    ensureBackgroundStage()
    /*
     * 메뉴 곡. **두 경로를 여기 한 줄로 덮는다.**
     *
     * 타이틀에서 오면 `onStart`가 이미 밝은 풀에서 곡을 뽑아 두었고, 판을 끝내고 오면
     * 무대가 접히면서 `session.dispose()`가 곡을 껐다(그리고 배경 무대는 `startActive: false`라
     * 스스로 뽑지 않는다). 그래서 직전까지 앞 경로는 곡이 흐르고 뒤 경로는 무음이었다.
     *
     * `start`를 그대로 부르면 앞 경로에서 **방금 튼 곡을 곧바로 다시 뽑아 끊는다.**
     * `ensurePool`이 "이 풀의 곡을 이미 쥐고 있는가"로 갈라 주며, 그 판단을 여기서 하지
     * 않는 이유는 `isPlaying()`이 로드 중·음소거에 false라 가드로 못 쓰기 때문이다
     * (`audio/bgm.ts`의 `ensurePool` 주석).
     *
     * **`ensureBackgroundStage()` 뒤여야 한다** - 그 안에서 무대가 갈리면 옛 세션의
     * `dispose()`가 곡을 끄므로, 앞에 두면 방금 이어 놓은 곡이 그 자리에서 죽는다.
     * 판이 시작되면 세션이 `bgm.start('play')`로 갈아 끼운다(판 곡이 메뉴 곡을 이긴다).
     */
    bgm.ensurePool('bright')
    renderMenu()

    /*
     * `?pull=seq`. 메뉴가 처음 선 뒤 곧바로 그 회차를 다시 보여 준다. 저장은 움직이지 않는다
     * (`accountEconomy.replay`). 한 번 보여 주고 비운다 - 닫고 메뉴로 돌아왔을 때 또 뜨면 못 닫는 화면이다.
     */
    if (pendingPull !== null) {
      const seq = pendingPull
      pendingPull = null
      showEnvelope(accountEconomy.replay(seq), true)
    }
  }

  /** 모두의 방으로(로드맵 M13). 배경은 메뉴와 같다 - 판이 돌지 않는 화면이라 무대를 갈지 않는다. */
  function showParty(): void {
    setScreen('party')
    hidePlates()
    stage?.session.setActive(false)
    partyHub.show()
  }

  /**
   * 열린 방 목록으로(M14). 화면을 **여기서 세우지 않는다** - 목록 화면과 그 소켓의 주인은 `app/lobbyFlow.ts`이고
   * (로비와 같다), 이 함수가 하는 일은 다른 판을 접고 무대를 재우는 것뿐이다.
   */
  function showBrowse(): void {
    setScreen('browse')
    hidePlates()
    stage?.session.setActive(false)
    bgm.ensurePool('bright')
  }

  /** 단계 선택으로. 배경에는 "지금 할 단계"의 방을 **멈춘 채로** 세워 둔다. */
  function showSelect(): void {
    setScreen('select')
    introLevelId = null
    /*
     * 안내 화면을 떠나면 고른 준비물도 놓는다(사물함은 건드리지 않는다 - 아직 쓰지 않았다).
     * 들고 나오면 다른 단계의 안내에 지난 선택이 얹히고, 그 카드는 화면 어디에도 눌린 표시가 없다.
     */
    kitFlow.reset()
    hidePlates()
    clearLocationQuery()

    ensureBackgroundStage()
    // 메뉴 곡을 이어 간다. 갈래와 이유는 `showMenu`의 같은 줄에 적었다(이 화면은 그 아래층이다).
    bgm.ensurePool('bright')
    renderSelect()
  }

  /* --- 봉투 · 사물함 ------------------------------------------------------------ */

  /** 열기 결과 → 봉투 화면의 뷰. "다음 장을 열 수 있나"의 판정은 경제 모듈의 것이고 여기서는 답만 옮긴다. */
  function envelopeViewOf(opened: OpenedEnvelopes, replay: boolean): EnvelopeView {
    const single = accountEconomy.canOpen(1)
    return {
      cards: opened.cards,
      spent: opened.spent,
      pinsBefore: opened.pinsBefore,
      pinsAfter: opened.pinsAfter,
      magazinesAfter: accountMagazines.count(),
      pityBefore: opened.pityBefore,
      next: {
        single: single.ok,
        ten: accountEconomy.canOpen(10).ok,
        roundsToAfford: single.ok ? 0 : single.roundsToAfford,
      },
      replay,
    }
  }

  function showEnvelope(opened: OpenedEnvelopes, replay = false): void {
    setScreen('envelope')
    hidePlates()
    stage?.session.setActive(false)
    envelopeScreen.show(envelopeViewOf(opened, replay))
  }

  /**
   * 봉투를 연다. 결제·뽑기·산출물 적용·저장은 `accountEconomy.open` 한 번이고, 여기는 그 결과를 화면에 넘긴다.
   * 핀이 모자라면 아무 일도 없다 - 선택 화면의 입구는 그 경우 잠겨 있고, 봉투 화면의 [한 장 더]는 이유를 말한다.
   */
  function openEnvelopes(count: 1 | 10): void {
    const opened = accountEconomy.open(count)
    if (opened === null) {
      console.warn(`[main] 핀이 모자라 봉투 ${String(count)}장을 열지 못했다(보유 ${String(accountEconomy.pins())}).`)
      return
    }
    showEnvelope(opened)
  }

  function showLocker(): void {
    setScreen('locker')
    hidePlates()
    stage?.session.setActive(false)
    locker.show({
      inventory: accountEconomy.inventory(),
      envelope: accountEconomy.envelope(),
      pins: accountEconomy.pins(),
    })
  }

  /**
   * 타이틀을 접고 실제 첫 화면으로 들어간다. **준비가 끝난 뒤에만 불린다.**
   *
   * 문을 닫는 일과 다음 화면을 여는 일을 한 함수에 묶어 둔 이유는 이 둘이 갈리면 "타이틀은
   * 접혔는데 뒤에 아무것도 없는" 프레임이 생기기 때문이다. 부르는 곳은 둘뿐이고
   * 순서만 다르다 - 준비가 먼저 끝났거나(첫 화면 블록), 버튼이 먼저 눌렸거나(`onStart`).
   */
  function enterFromTitle(): void {
    title.hide()
    showMenu()
  }

  /** 단계 안내로. 무대는 아직 바꾸지 않는다 - 시작 버튼을 누르기 전까지는 방을 만들 이유가 없다. */
  function showIntro(levelId: number): void {
    const level = getLevel(levelId)
    if (level === undefined) return

    setScreen('intro')
    introLevelId = levelId
    hidePlates()
    stage?.session.setActive(false)

    const previous = getLevel(levelId - 1)
    /*
     * **뷰를 먼저 만든다.** `viewFor`가 제시 셋 밖의 선택을 `null`로 접는 자리라(`app/kitFlow.ts`),
     * 숫자를 먼저 계산하면 그 방어가 걸리는 판에서 규칙 칸에는 준비물이 얹히고 카드에는 눌린 표시가 없는
     * 화면이 나온다 - 화면 어디에도 증상이 없는 종류다. 한 줄 순서가 곧 그 방어의 유효 범위다.
     */
    const kit = kitFlow.viewFor(level.id)
    /*
     * 지금 고른 준비물이 얹힌 값을 보여 준다(로드맵 M9). 카드를 누르면 이 함수가 다시 불리므로
     * 숫자가 그 자리에서 바뀐다 - "돋보기를 챙기면 무엇이 달라지는가"를 글이 아니라 숫자로 말하는 자리다.
     */
    const tuning = tuningFor(level, kitFlow.picked())
    levelIntro.show({
      level: levelSummary(level, TUNING),
      order: level.id,
      ammo: tuning.startAmmo,
      huntSeconds: tuning.huntSeconds,
      previousLevel: previous === undefined ? null : levelSummary(previous, TUNING),
      kit,
    })
  }

  /** 단계를 시작한다. 여기서만 무대가 단계용으로 다시 선다. */
  function startLevel(levelId: number): void {
    const level = getLevel(levelId)
    if (level === undefined) return

    setScreen('play')
    introLevelId = null
    hidePlates()
    /*
     * **여기서 오디오를 해제하지 않는다.** 이 앱의 해제는 타이틀 한 곳의 일이다 - 사람이
     * 여기까지 오는 길에는 [시작하기]가 반드시 있어 그 콜스택에서 이미 열렸다. 타이틀을
     * 세우지 않는 `?level=` 직행에는 애초에 제스처가 없으므로, 여기서 `unlock()`을 불러도
     * 열리는 것은 정지 상태의 컨텍스트뿐이다(그 경로는 `installBgmUnlockGesture`가 첫 탭에서 받는다).
     */
    writeLevelQuery(level.id)

    const next = getLevel(level.id + 1)
    /*
     * 시도 번호는 **저장된 판 수에서 온다**(`progress.nextAttemptFor`). 다시 들어올 때마다 0으로 돌아가면
     * 방이 고정인 단계는 매번 같은 정답표가 되고, 한 번 외운 사람에게 그 단계는 몇 초짜리 핀 복사기가 된다.
     */
    const startAttempt = takeStartAttempt() ?? nextAttemptFor(progress, level.id)
    mirrorAttempt(startAttempt)
    lastRoundPins = null

    /*
     * 준비물은 **여기서 빠진다**(로드맵 M9 - `app/kitFlow.ts` 머리말의 소비 시점).
     * 소비와 튜닝 적용이 같은 구간에 있어야 "썼는데 안 얹힌" 판이 생길 수 없다.
     *
     * 한 번의 소비가 덮는 것은 **이 무대 전체**다. 결과 화면의 재도전·"한 판 더"는 무대를 다시
     * 세우지 않으므로(세션 안에서 라운드만 돈다) 보너스가 그 단계에 머무는 동안 얹혀 있다.
     * 화면도 그렇게 말한다(`kit.pickHint`). 자세한 근거는 `kitFlow.ts` 머리말에 있다.
     */
    const kit = kitFlow.consume()

    swapStage({
      plan: getFloorPlan(level.planSize),
      tuning: tuningFor(level, kit),
      // 단계의 방 시드는 고정이다. 재도전에서 갈리는 것은 시도 번호(= 변화)뿐이다.
      seed: level.seed,
      startAttempt,
      targetObjectCount: level.objectTarget,
      magazines: accountMagazines.count(),
      onMagazineUsed: accountMagazines.spend,
      onSound: recordSound,
      level,
      hasNextLevel: next !== undefined,
      startActive: true,
      withEditor: devTools,
      onAttemptChange: (attempt) => {
        mirrorAttempt(attempt)
        // 재도전 = 새 판. 지난 판의 핀 줄이 다음 결과 화면에 남지 않게 여기서 비운다.
        lastRoundPins = null
      },
      onRoundEnd: (outcome) => saveOutcome(level, outcome),
      resultPins: () => lastRoundPins,
      onNextLevel: () => {
        if (next !== undefined) showIntro(next.id)
      },
      onExitToSelect: () => showSelect(),
    })
  }

  /**
   * 매일의 방 한 판(로드맵 M9). 오늘의 날짜 키가 방 시드이고 시도 번호는 0에 고정이라,
   * **같은 날 문을 연 사람은 모두 같은 방에서 같은 이상한 곳을 찾는다**(M9 규칙 1).
   *
   * 단계를 넘기지 않는다(`level`이 없다). 매일의 방은 커리큘럼 밖이고, 단계 곡선을 얹으면
   * "오늘의 방은 몇 단계인가"라는 답할 수 없는 질문이 생긴다 - 기본 튜닝은 곧 졸업 규칙이자
   * 여럿이 규칙이라 그 자리가 맞다. 평면도는 단계와 무관한 방과 같은 것(아파트)을 쓴다.
   *
   * 준비물을 넘길 자리가 없다 - `dailyFlow.tuning()`이 부르는 `resolveDailyTuning`에 그 인자가 아예 없다
   * (M9 규칙 3). 주소창도 건드리지 않는다: 오늘의 방을 재현하는 열쇠는 시드가 아니라 **날짜**이고,
   * 그 손잡이는 `?dev=1&date=`다.
   *
   * **탄창도 넣지 않는다.** 준비물을 뺀 이유가 그대로 여기에도 걸린다 - 탄창은 봉투에서 나오는 계정
   * 저장값이고 라운드 중 재장전을 허용하므로(`gameState.onUseMagazine`), 세 장 쟁여 둔 사람과 0장인
   * 사람이 같은 날짜 키의 같은 방을 **다른 탄환 예산**으로 돌게 된다. 그러면 그날의 점수를 견줄 수 없고,
   * 견주기가 매일의 방의 존재 이유다. `magazines`를 생략하면 0이다(`session.ts`의 규약).
   */
  function startDaily(): void {
    setScreen('play')
    introLevelId = null
    hidePlates()
    lastRoundPins = null

    swapStage({
      plan: FREE_MODE_PLAN,
      tuning: dailyFlow.tuning(),
      seed: dailyFlow.seed,
      startAttempt: dailyFlow.attempt,
      onSound: recordSound,
      startActive: true,
      withEditor: devTools,
      /*
       * 방이 고정이다(`session.lockedRoom`). "한 판 더"가 오늘의 방을 다시 세우고 "다른 변화로 다시"는
       * 만들어지지 않는다 - 둘 다 날짜가 낼 수 없는 방으로 나가는 문이었다.
       */
      lockedRoom: true,
      onRoundEnd: saveDailyOutcome,
      resultPins: () => lastRoundPins,
      // 매일의 방은 메뉴에서 왔다. 단계 목록은 이 판과 아무 관계가 없다(커리큘럼 밖이다).
      onExitToSelect: () => showMenu(),
    })
  }

  /**
   * 매일의 방 한 판의 결말. 기록·출석·핀은 `app/dailyFlow.ts`가 한 덩어리로 확정하고 여기는 화면 몫만 옮긴다.
   *
   * 그날의 첫 완주가 아니면 핀 줄을 **비운다** - 연습 판의 결과 화면이 지난 판의 핀을 말하면
   * "다시 해도 핀이 붙는다"로 읽힌다(첫 결과만 기록한다는 규칙이 화면에서 뒤집힌다).
   */
  function saveDailyOutcome(outcome: RoundOutcome): void {
    const pins = dailyFlow.finish(outcome)
    if (pins === null) {
      lastRoundPins = null
      return
    }
    lastRoundPins = {
      earned: pins.total,
      total: accountEconomy.pins(),
      price: accountEconomy.price().single,
      shortBy: shortByOf(accountEconomy.canOpen(1)),
    }
  }

  /** 자유 모드. 단계와 무관한 아무 방이며 튜닝 실험용이다(9.5절의 "개발자의 테스트 도구"). */
  function startFreeRound(seed: string): void {
    setScreen('play')
    hidePlates()
    // 해제는 부르지 않는다. 자유 모드는 `?seed=` 직행뿐이라 제스처가 없고, 제스처 밖의
    // `unlock()`은 정지된 컨텍스트만 만든다. 첫 탭은 `installBgmUnlockGesture`가 받는다.
    writeSeedQuery(seed)

    // 자유 모드에는 단계 기록이 없다(핀도 붙지 않는다 - `onRoundEnd`가 없다). 첫 시도에서 시작한다.
    const startAttempt = takeStartAttempt() ?? FIRST_ATTEMPT
    mirrorAttempt(startAttempt)

    swapStage({
      plan: FREE_MODE_PLAN,
      tuning: resolveTuning(search),
      seed,
      startAttempt,
      // 자유 모드도 같은 계정을 쓴다. 여기서만 무한히 주면 튜닝 실험이 실제 판과 다른 조건이 된다.
      magazines: accountMagazines.count(),
      onMagazineUsed: accountMagazines.spend,
      onSound: recordSound,
      startActive: true,
      withEditor: devTools,
      onAttemptChange: mirrorAttempt,
      // "한 판 더"는 새 방이므로 시도 번호가 0으로 돌아간다. 주소창도 함께 정리해야
      // 새로고침했을 때 지난 방의 회차가 새 방에 얹히지 않는다.
      onSeedChange: (next) => {
        writeSeedQuery(next)
        writeAttemptQuery(FIRST_ATTEMPT)
      },
    })
  }

  /**
   * 봇과 한 판(로드맵 M2). 루프백 전송으로 호스트를 같은 탭에 세우고, 나와 봇 `bots`명이 같은 방을 수색한다.
   *
   * 자유 모드와 같은 무대 절차를 밟되 `match`가 붙는다 - 시도 번호는 없다(방 코드 하나가 판을 재현한다. 여럿이의 변화
   * 시드는 호스트가 시드에서 파생하므로 `?attempt=`는 뜻이 없다). 문은 셋이고 전부 여기로 온다 - 선택 화면의 "여럿이" 카드,
   * `?match=bots` 직행, 개발 훅 `__weirdRoom.startBotMatch`.
   *
   * 결과 화면의 "한 판 더"는 세션이 스스로 `nextRound`를 보내므로(`matchSession.ts`) 여기 콜백이 없다. "나가기"만 앱의 것이다 -
   * 방을 떠나 단계 선택으로 돌아오는 것은 화면 전환이고, 그때 `clearLocationQuery`가 봇 판의 주소를 지운다.
   */
  function startBotMatch(request: BotMatchRequest): void {
    setScreen('play')
    hidePlates()
    // 소켓 방을 쥐고 있었다면 놓는다 - "어느 판인가"는 하나여야 한다(주소창도 `writeMatchQuery`가 `?room=`을 지운다).
    lobbyFlow.leave()
    // 시드와 봇 셋을 한 번에 적는다 - 새로고침이 같은 판(같은 방, 같은 봇 행동)으로 돌아오는 것이 M2의 완료 판정이다.
    writeMatchQuery(request)

    const tuning = resolveTuning(search)
    /*
     * 봇 판의 평면도도 좌석 수의 함수다(로드맵 M11) - 나 하나 + 이웃 `bots`명. 소켓 방과 달리 좌석이 판을 세우기 전에
     * 이미 정해져 있어(`?bots=`) 여기서 한 번 굽고 무대와 호스트에 **같은 것**을 넘긴다. 봇 수를 여기서 다시 깎지 않는
     * 이유는 `partyPlanForSeats`가 표의 범위로 접기 때문이다 - 개발 훅이 넘긴 20이 8석 평면도가 된다.
     */
    const plan = partyPlanForSeats(request.bots + 1)
    /*
     * 루프백 전송을 **여기서** 만든다(로드맵 M6). M2~M5에서는 무대가 봇 수를 받아 안에서 만들었지만, 소켓 방은 전송이 무대보다
     * 먼저 생기므로(`welcome`을 받아야 내 좌석을 안다) 그 자리가 갈렸다 - 무대는 이제 "어떤 전송이든" 받는다(`stageMatch.ts` 머리말).
     * 봇 판의 동작은 한 글자도 달라지지 않는다: 같은 시드, 같은 좌석 순서, 같은 `deps`.
     */
    swapStage({
      plan,
      tuning,
      seed: request.seed,
      match: createLoopbackMatch({
        seed: request.seed,
        tuning,
        plan,
        magazines: accountMagazines.count(),
        bots: request.bots,
        botLevel: request.botLevel,
      }),
      magazines: accountMagazines.count(),
      onMagazineUsed: accountMagazines.spend,
      // 봇 판도 여럿이다 - 바꾸기 단계가 서고 사람이 그 띠를 처음 만나는 자리다(`ui/changerCoach.ts` 머리말).
      needsCoach,
      onCoached: markCoached,
      onSound: recordSound,
      startActive: true,
      withEditor: devTools,
      onExitToSelect: () => showMenu(),
    })

    /*
     * 로비를 화면 없이 지난다 - `ready` 한 번, 전원 준비면 `start` 한 번(M2의 두 줄. 사람 로비에서는 이 자리를 UI가 대신한다).
     * 세션이 이미 구독한 **뒤**에 붙는다: 전송은 배달을 마이크로태스크로 미루므로 이 줄까지가 같은 동기 구간이고,
     * 그래서 첫 `welcome`을 세션이 먼저, 자동 진행이 그다음에 본다.
     */
    const match = stage?.match
    if (match !== null && match !== undefined) stopAutopilot = lobbyFlow.autopilot(match)
  }

  /**
   * 한 판의 결과를 진행에 반영한다.
   *
   * 저장 실패는 **멈추지 않는다**(`progress.ts`의 계약). 대신 콘솔에 한 줄 남긴다 -
   * 사생활 보호 모드에서 "다음 단계가 안 열린다"는 제보를 받았을 때 볼 곳이 있어야 한다.
   */
  function saveOutcome(level: LevelDef, outcome: RoundOutcome): void {
    // 첫 클리어인가는 **커밋 전** 기록으로 판단한다. 커밋 뒤에 물으면 언제나 "이미 깼다"가 되어 첫 클리어 핀이 영영 안 붙는다.
    const firstClear = getLevelRecord(progress, level.id)?.cleared !== true

    const committed = commitLevelResult(progress, level, {
      foundCount: outcome.foundCount,
      score: outcome.score,
      elapsedMs: outcome.elapsedMs,
      /*
       * 이 판의 이상한 곳 수까지 넘긴다. 기록표가 "12개 중 9개"라고 말하려면 분모가 필요한데,
       * 오늘의 튜닝에서 다시 읽으면 개수가 바뀌는 날 옛 기록이 거짓말이 된다(`progress.ts`).
       */
      changeCount: outcome.changeCount,
    })

    progress = committed.progress
    if (!committed.saved) {
      console.warn('[main] 진행을 저장하지 못했다. 이번 세션에서는 다음 단계로 갈 수 있다.')
      /*
       * 콘솔만으로는 부족하다. 사생활 보호 모드에서는 **새로고침 한 번에 이 판이 사라지는데**
       * 화면이 아무 말도 하지 않으면 그 사실을 알 방법이 없다(다음에 열었을 때 단계가 잠긴
       * 것만 보인다). 결과 화면 위에 뜨는 토스트 한 줄이 그 경고다.
       */
      stage?.session.showNotice(t('error.saveFailed'), 'warn')
    }

    /*
     * 핀은 라운드 결과에서만 생긴다(로드맵 3.1절). 산식은 `economy/pins.ts` 하나이고 여기서는 부르고 저장할 뿐이다.
     * 단계 기록 저장과 갈라 저장하는 것은 감수한다 - 둘이 한 덩어리가 아니어도 "클리어는 됐는데 핀이 없다"는
     * 다음 판이 메우는 손해이고, 반대로 핀 때문에 클리어 저장이 미뤄지는 쪽이 더 나쁘다.
     */
    /*
     * 클리어마다 준비물 하나를 확정으로 준다(로드맵 M9 - `economy/kitDraft.grantClearKit`).
     * 봉투에서만 나오면 초반 플레이어가 3택1을 몇 판 동안 못 보고, 그러면 그 화면은 빈손 문구 하나로만 존재한다.
     * 무엇을 주는지는 여기서 정하지 않는다 - 값도 등급도 그 파일의 것이다.
     */
    if (outcome.cleared) accountEconomy.grantClearKit()

    const pins = pinsForOutcome(
      { foundCount: outcome.foundCount, cleared: outcome.cleared, perfect: outcome.perfect },
      { firstClear },
    )
    accountEconomy.earn(pins.total)
    lastRoundPins = {
      earned: pins.total,
      total: accountEconomy.pins(),
      price: accountEconomy.price().single,
      shortBy: shortByOf(accountEconomy.canOpen(1)),
    }
  }

  /* --- 입력 --------------------------------------------------------------------- */

  /**
   * 조작 레이어·제스처·키보드를 한꺼번에 세운다. **무대를 값으로 주지 않는다** -
   * 무대는 판마다 갈리므로 조회 함수라야 한다(`app/inputLayer.ts`).
   *
   * 이 줄이 `swapStage`보다 **아래**에 있어도 되는 이유: 첫 무대는 아래 첫 화면 블록에서야
   * 서고, 타이틀의 [시작하기]도 그 블록이 `bootReady`를 올리기 전까지는 화면을 옮기지 않는다.
   */
  const input = createInputLayer({
    ctx,
    canvas,
    devTools,
    getStage: () => stage,
    getScreen: () => screen,
  })

  /*
   * 언어가 바뀌면 문서 바깥쪽을 맞추고, 스스로 다시 그릴 줄 모르는 화면을 다시 세운다.
   * HUD·조작 레이어·결과 화면은 각자 `onLocaleChange`를 구독하므로 여기서 건드리지 않는다.
   */
  const stopLocaleWatch = onLocaleChange(() => {
    syncDocumentMeta()
    rebuildOverlays()
  })

  /**
   * 한 프레임. `startRenderLoop`가 부르지만 **이름을 붙여 따로 둔다** -
   * 배경 탭·임베드 프리뷰에서는 rAF가 멈춰 있어서, 검증할 때 한 프레임을 손으로 돌릴 수단이 필요하다
   * (개발 훅의 `step`). 루프와 검증이 같은 함수를 쓰지 않으면 검증이 검증이 아니게 된다.
   *
   * 세션이 **먼저** 도는 이유: 리빌 연출이 이번 프레임의 카메라 목표를 정한다.
   * 뒤에 두면 `iso.update`가 지난 프레임의 목표로 보간해 연출이 한 프레임씩 밀린다.
   */
  function frame(dt: number): void {
    const current = stage
    if (current === null) return

    /*
     * 설정 패널이 열려 있는 동안 **라운드는 멈춘다.**
     *
     * 규칙을 한 줄도 건드리지 않고 프레임 공급만 끊는다 - 리듀서에 시간이 들어가지 않으므로
     * 페이즈 시계·쿨타임·리빌 타임라인이 전부 그 자리에 선다. `session.setActive(false)`를
     * 쓰지 않는 이유는 그쪽이 훨씬 무겁기 때문이다(HUD를 접고, 연출을 접고, 정답 방에서
     * 강제 퇴장시킨다). 여기서 필요한 것은 "시간이 흐르지 않는다" 하나뿐이다.
     *
     * 오조작으로 열렸을 때 잃는 것이 0이어야 한다는 것이 근거다. 수색 5분짜리 판에서 설정을
     * 잘못 열어 30초를 잃으면 그 판은 버려진다. 카메라는 계속 보간한다 - 멈춘 화면이
     * 굳어 보이지 않게 하는 값이고, 그 값은 판정에 들어가지 않는다.
     */
    if (!isSettingsOpen()) current.session.update(dt)
    current.iso.update(dt)

    /*
     * 컷어웨이는 **카메라가 지금 실제로 서 있는 방향**을 본다(`getVisualDirection`).
     *
     * 목표 방향(`getDirection`)을 주면 `setDirection`을 부른 순간 벽이 걷히는데, 카메라는
     * 0.3초에 걸쳐 보간되므로 그 동안 **있어야 할 벽이 없는** 화면이 된다. 반대로 회전이
     * 끝난 뒤에 걷으면 도는 내내 앞벽이 방을 가린다. 두 실패 사이의 유일한 정답이
     * "방위각의 중간에서 갈아 끼우기"이고, 그 판단은 방위각을 쥔 카메라가 한다 -
     * 여기서 진척도를 받아 판정하면 직전 방향을 기억하는 상태가 이 배선 코드에 생긴다.
     */
    current.cutaway.update(current.iso.getVisualDirection())
    current.cutaway.advance(dt)

    input.refresh()
  }

  const stopRenderLoop = startRenderLoop(ctx, frame)

  /* --- 첫 화면 ---------------------------------------------------------------- */

  if (directLevelId !== null) {
    // `?level=N`은 안내도 건너뛴다. 수십 번 돌릴 도구에서 Enter 한 번은 그대로 마찰이다.
    startLevel(directLevelId)
  } else if (botMatch !== null) {
    // `?match=bots`. 타이틀도 선택 화면도 없이 봇 판이 선다(같은 `?seed=`면 같은 봇 행동 - 재현 도구다).
    startBotMatch(botMatch)
  } else if (directRoomCode !== null) {
    /*
     * `?room=ABCD`(로드맵 M6). 이 탭이 그 방에 앉았던 기억이 있으면 곧바로 자리로 돌아가고(새로고침 복귀 - 완료 판정의 한 줄),
     * 없으면 이름을 적는 폼이 그 번호로 채워진 채 선다(옆 사람이 보낸 링크로 처음 들어온 경우).
     */
    openLobby(directRoomCode)
  } else if (freeModeSeed !== null) {
    startFreeRound(freeModeSeed)
  } else {
    /*
     * 타이틀은 부팅 첫 줄에서 이미 떠 있다(위 타이틀 절). 여기서 하는 일은 **문을 열어 주는
     * 것**뿐이다 - 이 줄 아래로는 시작을 받을 수 있다.
     */
    bootReady = true
    if (startRequested) enterFromTitle()
    // 아직 안 눌렸다면 남는 시간에 배경 방을 미리 굽는다. 눌린 뒤에 구우면 그 시간이 마찰이다.
    else ensureBackgroundStage()
  }

  exposeDebugHandle(ctx, {
    getStage: () => stage,
    getScreen: () => screen,
    getDevTools: () => devTools,
    // 사람의 탭과 **같은 함수**를 내준다. 두 벌이면 자동 검증이 자기 자신을 검증하게 된다.
    tapAt: input.tapAt,
    step: frame,
    setMode: input.setMode,
    startLevel,
    startBotMatch: (seed, bots, botLevel) => startBotMatch({ seed, bots, botLevel }),
    showMenu,
    showSelect,
    showParty,
    showIntro,
    getMagazines: accountMagazines.count,
    grantMagazines: accountMagazines.grant,
    getPins: accountEconomy.pins,
    grantPins: accountEconomy.earn,
    openEnvelope: openEnvelopes,
    showLocker,
    getSounds: () => soundLog.map((entry) => ({ ...entry })),
    clearSounds: () => {
      soundLog.length = 0
    },
    setMuted: (muted: boolean) => {
      audio.setMuted(muted)
      if (!muted) audio.unlock()
      settings.refresh()
    },
  })

  return () => {
    stopRenderLoop()
    // 끊지 않으면 떼어 낸 화면을 붙들고 있는 클로저가 사전 쪽에 영원히 남는다.
    stopLocaleWatch()
    stopUnlockGesture()
    stopBgmGesture()

    // 조작 레이어·제스처·키보드가 한 줄로 걷힌다(`app/inputLayer.ts`의 `dispose`).
    input.dispose()
    // 소켓·로비 화면·연결 배너가 한 줄로 걷힌다. 무대보다 먼저다 - 나중이면 걷힌 무대에 `welcome`이 도착할 수 있다.
    lobbyFlow.dispose()
    title.dispose()
    menu.dispose()
    partyHub.dispose()
    levelSelect.dispose()
    levelIntro.dispose()
    envelopeScreen.dispose()
    locker.dispose()
    settings.dispose()
    stage?.dispose()
    stage = null

    ctx.renderer.dispose()
  }
}

bootstrap().then(
  (dispose) => {
    // 개발 중 모듈이 갈릴 때 리스너와 HUD가 겹쳐 쌓이는 것을 막는다.
    // (HUD가 두 겹이면 탭이 위쪽 것에만 먹혀 "버튼이 안 눌린다"로 보인다.)
    import.meta.hot?.dispose(dispose)
  },
  (error: unknown) => {
    // 조용히 삼키면 검은 화면만 남는다. 화면에 띄우고, 스택은 콘솔에 그대로 남긴다.
    reportFatalError(error)
    console.error('[main] 부트스트랩 실패', error)
  },
)
